// Списъкът на „Целия списък": лепнатият хедър (отметката „всички видими" с три
// състояния + имената на колоните), прозорецът с редовете (useCatalogWindow —
// рендират се само видимите) и празното състояние с действие: „Покажи от
// всички групи" (има съвпадения другаде) или „+ Нов продукт „…"" (листът го
// поддържа). И двете се викат и с Enter от филтъра — подсказката го казва.
// Списъкът е `@container`: колоната „Мин." се показва само в широк списък.
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { CheckBox } from "../filters/CheckBox";
import { KbdChip } from "../KbdChip";
import { SECONDARY_BUTTON_CLASS } from "../list/buttonStyles";
import { ALL_GROUPS, isSelectable, NO_GROUP } from "./catalogPickerModel";
import {
  CATALOG_CHECK_CELL_CLASS,
  CATALOG_HEADER_CLASS,
  CATALOG_MINIMUM_CELL_CLASS,
  CATALOG_NARROW_GUTTER_CLASS,
  CATALOG_PRICE_CELL_CLASS,
  CATALOG_QTY_CELL_CLASS,
  CATALOG_ROW_GUTTER_CLASS,
  CATALOG_STOCK_CELL_CLASS,
  CATALOG_SUM_CELL_CLASS,
  CATALOG_UNIT_CELL_CLASS,
  catalogOptionId,
} from "./catalogPickerLayout";
import { CatalogPickerRow } from "./CatalogPickerRow";
import type { CatalogPickerState } from "./useCatalogPicker";

type Props = {
  picker: CatalogPickerState;
  listId: string;
  isNarrow: boolean;
  addedSelectable: boolean;
  itemCount: number;
  priceLabel?: string;
};

export const CatalogPickerList = ({
  picker,
  listId,
  isNarrow,
  addedSelectable,
  itemCount,
  priceLabel,
}: Props) => {
  const { t } = useTranslation();
  const { visible, columns, selection, activeIndex, picked, words, variant, listWindow, entry, entries } = picker;
  const inDocumentLabel = t("inventoryPage.catalogInDocument");
  const groupLabel =
    picker.group === NO_GROUP ? t("inventoryPage.stocktakeNoGroup") : picker.group;
  // групата до името — само когато се гледат всички групи
  const showGroupInRow = picker.group === ALL_GROUPS;
  const withPrice = columns.price || entry?.kind === "qtyPrice";
  const qtyLabel = entry?.qtyLabel ?? "";
  const fieldLabels = useMemo(() => ({ qty: qtyLabel, price: t("inventoryPage.catFieldPrice") }), [qtyLabel, t]);
  // Стабилен за memo на редовете: последната версия на клавишите на полетата
  const fieldKeyDownRef = useRef(picker.onFieldKeyDown);
  fieldKeyDownRef.current = picker.onFieldKeyDown;
  const onFieldKeyDown = useCallback(
    (index: number, event: React.KeyboardEvent<HTMLInputElement>) => fieldKeyDownRef.current(index, event),
    [],
  );

  // Отметката „всички видими" (три състояния). На телефон е с надпис и целият
  // ред я натиска; на десктоп надписът е в title/aria-label.
  const selectAll = (
    <button
      type="button"
      role="checkbox"
      aria-checked={selection.state === "all" ? true : selection.state === "some" ? "mixed" : false}
      aria-label={t("inventoryPage.catAllVisible")}
      title={t("inventoryPage.catAllVisible")}
      disabled={!selection.selectableCount}
      onMouseDown={(event) => event.preventDefault()}
      onClick={picker.toggleVisible}
      className={`flex cursor-pointer items-center gap-3 rounded border-none bg-transparent p-0 text-inherit outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] disabled:cursor-default disabled:opacity-40 ${
        isNarrow ? "min-w-0 flex-1" : ""
      }`}
    >
      <span className={CATALOG_CHECK_CELL_CLASS}>
        <CheckBox checked={selection.state === "all"} indeterminate={selection.state === "some"} />
      </span>
      {isNarrow && <span className="truncate">{t("inventoryPage.catAllVisible")}</span>}
    </button>
  );

  const header = (
    <div
      data-catalog-header
      className={`sticky top-0 z-[2] flex items-center border-b border-shade-3 bg-fill ${CATALOG_HEADER_CLASS} ${
        isNarrow ? CATALOG_NARROW_GUTTER_CLASS : CATALOG_ROW_GUTTER_CLASS
      }`}
    >
      {selectAll}
      {isNarrow ? null : !columns.structured ? (
        <span className="min-w-0 flex-1 truncate">{t("inventoryPage.colProductName")}</span>
      ) : (
        <>
          <span className="min-w-0 flex-1 truncate">{t("inventoryPage.colProductName")}</span>
          {columns.unit && <span className={CATALOG_UNIT_CELL_CLASS}>{t("inventoryPage.colUnitShort")}</span>}
          {columns.stock && <span className={CATALOG_STOCK_CELL_CLASS}>{t("inventoryPage.colAvailable")}</span>}
          {columns.minimum && (
            <span className={CATALOG_MINIMUM_CELL_CLASS} title={t("inventoryPage.colMinQty")}>
              {t("inventoryPage.catColMin")}
            </span>
          )}
          {entry && <span className={CATALOG_QTY_CELL_CLASS}>{entry.qtyLabel}</span>}
          {withPrice && (
            <span
              className={CATALOG_PRICE_CELL_CLASS}
              title={entry?.kind === "qtyPrice" ? t("inventoryPage.catFieldPriceHint") : undefined}
            >
              {priceLabel ?? t("inventoryPage.catColPrice")}
            </span>
          )}
          {entry?.kind === "qtyPrice" && <span className={CATALOG_SUM_CELL_CLASS}>{t("inventoryPage.catColSum")}</span>}
        </>
      )}
    </div>
  );

  // ─── Празно състояние ────────────────────────────────────────────────────
  const emptyState = () => {
    let message = t("inventoryPage.filterNoMatch");
    let action: { label: string; icon?: React.ReactNode; run: () => void } | null = null;
    if (!itemCount) message = t("inventoryPage.catEmpty");
    else if (picker.enterOnEmpty === "allGroups") {
      message = t("inventoryPage.catNoMatchInGroup", { group: groupLabel });
      action = {
        label: t("inventoryPage.catShowAllGroups", { count: picker.view.total }),
        run: () => picker.chooseGroup(ALL_GROUPS, !isNarrow),
      };
    }
    if (!action && picker.enterOnEmpty === "create")
      action = {
        label: t("inventoryPage.catCreateNamed", { name: picker.query.trim() }),
        icon: <PlusOutlined />,
        run: () => picker.create?.(),
      };
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        <span className="flex h-[46px] w-[46px] items-center justify-center rounded-[13px] bg-brand-tint text-[20px] text-brand-deep-ink">
          <SearchOutlined aria-hidden />
        </span>
        <span className="text-[12.5px] text-muted">{message}</span>
        {action && (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={action.run}
            className={`${SECONDARY_BUTTON_CLASS} max-w-full`}
          >
            {action.icon}
            <span className="truncate">{action.label}</span>
            {!isNarrow && <KbdChip>Enter</KbdChip>}
          </button>
        )}
      </div>
    );
  };

  const rows = visible.slice(listWindow.first, listWindow.last);
  return (
    <div
      ref={listWindow.setScrollElement}
      onScroll={listWindow.onScroll}
      className="@container min-h-0 flex-1 overflow-y-auto overscroll-contain bg-surface"
    >
      {header}
      <div id={listId} role="listbox" aria-multiselectable aria-label={t("inventoryPage.catalogOpen")}>
        {/* Празнините пазят височината на скрола — стойността е по време на изпълнение */}
        <div aria-hidden style={{ height: listWindow.topSpacer }} />
        {rows.map((item, offset) => {
          const index = listWindow.first + offset;
          const isPicked = picked.has(item.id);
          return (
            <CatalogPickerRow
              key={item.id}
              item={item}
              index={index}
              rowCount={visible.length}
              optionId={catalogOptionId(listId, item.id)}
              variant={variant}
              columns={columns}
              words={words}
              picked={isPicked}
              active={index === activeIndex}
              selectable={isSelectable(item, addedSelectable)}
              inDocumentLabel={inDocumentLabel}
              groupLabel={showGroupInRow ? item.group : null}
              onToggle={picker.toggleItem}
              onHover={isNarrow ? undefined : picker.setActiveIndex}
              entry={entry}
              entryValue={isPicked ? entries.get(item.id) : undefined}
              onEdit={entry ? picker.editItem : undefined}
              fieldLabels={entry ? fieldLabels : undefined}
              onFieldChange={entry ? picker.onFieldChange : undefined}
              onFieldKeyDown={entry ? onFieldKeyDown : undefined}
              onFieldFocus={entry ? picker.setActiveIndex : undefined}
            />
          );
        })}
        <div aria-hidden style={{ height: listWindow.bottomSpacer }} />
      </div>
      {visible.length === 0 && emptyState()}
    </div>
  );
};

export default CatalogPickerList;
